import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import { X, Send, Clock, User, Phone, Mail } from 'lucide-react';
import { format } from 'date-fns';
import { sendSMS } from '../lib/messaging';
import { sendGmailEmail } from '../lib/email';
import { getEmailPreview } from '../lib/emailParser';

interface Message {
  id: string;
  business_id: string;
  customer_id: string | null;
  message_type: 'sms' | 'email';
  direction: 'inbound' | 'outbound';
  from_number: string;
  to_number: string;
  body: string;
  subject?: string;
  read: 'read' | 'unread';
  created_at: string;
  updated_at: string;
}

interface ModernMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  customerName: string;
  customerPhone?: string | null;
  customerEmail?: string | null;
}

const formatPhoneNumber = (phone?: string): string => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Format based on length
  if (cleaned.length === 10) {
    return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.substring(1, 4)}) ${cleaned.substring(4, 7)}-${cleaned.substring(7)}`;
  }
  
  // Return original if it doesn't match expected format
  return phone;
};

export default function ModernMessageModal({
  isOpen,
  onClose,
  customerId,
  customerName,
  customerPhone,
  customerEmail
}: ModernMessageModalProps) {
  const { selectedBusiness } = useBusiness();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [messageType, setMessageType] = useState<'sms' | 'email'>('sms');
  const [replyText, setReplyText] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [availableChannels, setAvailableChannels] = useState<{
    sms: boolean;
    email: boolean;
  }>({ sms: false, email: false });
  const [locallyReadMessages, setLocallyReadMessages] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Function to scroll messages to bottom
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Determine which communication channels are available
  useEffect(() => {
    const channels = {
      sms: Boolean(customerPhone),
      email: Boolean(customerEmail)
    };
    
    setAvailableChannels(channels);
    
    // Set default message type based on available channels
    if (channels.sms) {
      setMessageType('sms');
    } else if (channels.email) {
      setMessageType('email');
    }
  }, [customerPhone, customerEmail]);

  // Fetch messages when the modal opens
  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      fetchMessages();
    }
  }, [isOpen, selectedBusiness?.id, customerId]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  // Set up realtime subscription for live updates
  useEffect(() => {
    if (!isOpen || !selectedBusiness?.id || !supabase) return;

    console.log('[MODAL REALTIME] Setting up subscription for customer:', customerId);

    // Create the subscription
    const subscription = supabase
      .channel(`customer_messages_${customerId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'all_messages',
          filter: `business_id=eq.${selectedBusiness.id}`
        },
        (payload) => {
          const message = payload.new as Message;
          
          // Only update if the message is for this customer
          if (message && message.customer_id === customerId) {
            console.log('[MODAL REALTIME] New message for customer:', customerId);
            
            if (payload.eventType === 'INSERT') {
              setMessages(prevMessages => {
                if (prevMessages.some(msg => msg.id === message.id)) {
                  return prevMessages;
                }
                return [...prevMessages, message].sort(
                  (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                );
              });
            } else if (payload.eventType === 'UPDATE') {
              setMessages(prevMessages =>
                prevMessages.map(msg =>
                  msg.id === message.id ? message : msg
                )
              );
            }
          }
        }
      )
      .subscribe();

    return () => {
      console.log('[MODAL REALTIME] Cleaning up subscription');
      subscription.unsubscribe();
    };
  }, [isOpen, selectedBusiness?.id, customerId]);

  const fetchMessages = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    setLoading(true);
    
    try {
      // Fetch messages for this specific customer
      let query = supabase
        .from('all_messages')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .eq('customer_id', customerId)
        .order('created_at', { ascending: true });
      
      const { data, error } = await query;
      
      if (error) {
        throw error;
      }
      
      setMessages(data || []);
      
      // Mark all messages as read when viewing
      if (data && data.length > 0) {
        const unreadIds = data
          .filter(msg => msg.read === 'unread' && msg.direction === 'inbound')
          .map(msg => msg.id);
        
        if (unreadIds.length > 0) {
          await markMessagesAsRead(unreadIds);
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markMessagesAsRead = async (messageIds: string[]) => {
    try {
      if (!supabase || messageIds.length === 0) return;

      // Update locally first
      setLocallyReadMessages(prev => new Set([...prev, ...messageIds]));

      // Update database
      const { error } = await supabase
        .from('all_messages')
        .update({ read: 'read' })
        .in('id', messageIds);

      if (error) {
        console.error('Error marking messages as read:', error);
        // Revert local state
        setLocallyReadMessages(prev => {
          const newSet = new Set(prev);
          messageIds.forEach(id => newSet.delete(id));
          return newSet;
        });
      } else {
        // Dispatch event to update navigation badge
        window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const handleSendSMS = async () => {
    if (!replyText.trim() || !selectedBusiness?.id || !customerPhone) return;
    
    try {
      setSendingMessage(true);
      
      const result = await sendSMS({
        to: customerPhone,
        message: replyText,
        businessId: selectedBusiness.id
      });
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      // Clear input
      setReplyText('');
      
      // Refresh messages to get the sent message
      await fetchMessages();
    } catch (error) {
      console.error('Error sending SMS:', error);
      alert('Failed to send SMS. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };



  const handleSendEmail = async () => {
    if (!replyText.trim() || !selectedBusiness?.id || !customerEmail) return;
    
    try {
      setSendingMessage(true);
      
      const result = await sendGmailEmail({
        businessId: selectedBusiness.id,
        to: customerEmail,
        subject: `Message from ${selectedBusiness.name || 'Business'}`,
        body: replyText
      });
      
      if (result.success) {
        setReplyText('');
        await fetchMessages();
      } else {
        throw new Error(result.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (messageType === 'sms') {
        handleSendSMS();
      } else {
        handleSendEmail();
      }
    }
  };

  const getAvailableReplyMethods = () => {
    const methods = [];
    if (customerPhone) {
      methods.push({ type: 'sms' as const, label: 'SMS', available: true });
    }
    if (customerEmail) {
      methods.push({ type: 'email' as const, label: 'Email', available: true });
    }
    return methods;
  };

  if (!isOpen) return null;

  const availableReplyMethods = getAvailableReplyMethods();

  return (
    <div className="fixed inset-0 z-[3000] overflow-y-auto">
      <div className="flex min-h-screen items-center sm:items-center p-0 sm:p-4 text-center">
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-full sm:max-w-2xl sm:w-full m-0 sm:m-auto mt-10 sm:mt-0 max-h-[90vh] sm:max-h-none">
          {/* Modal Header */}
          <div className="bg-white px-4 py-3 border-b border-gray-200 flex justify-between items-center">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden mr-3">
                <User className="h-5 w-5 text-gray-500" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {customerName}
                </h3>
                <div className="flex flex-col sm:flex-row gap-2 text-sm text-gray-600">
                  {customerPhone && (
                    <div className="flex items-center">
                      <Phone className="h-3.5 w-3.5 mr-1 text-gray-500" />
                      <span>{formatPhoneNumber(customerPhone)}</span>
                    </div>
                  )}
                  {customerEmail && (
                    <div className="flex items-center">
                      <Mail className="h-3.5 w-3.5 mr-1 text-gray-500" />
                      <span>{customerEmail}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Message List */}
          <div className="bg-gray-50 h-[50vh] sm:h-96 overflow-y-auto p-4">
            {loading ? (
              <div className="flex justify-center items-center h-full">
                <div className="text-gray-500">Loading messages...</div>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex justify-center items-center h-full">
                <div className="text-gray-500">No messages found</div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => {
                  const isRead = message.read === 'read' || locallyReadMessages.has(message.id);
                  
                  return (
                    <div 
                      key={message.id}
                      className={`flex ${message.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div 
                        className={`max-w-[75%] p-3 rounded-lg relative ${
                          message.direction === 'outbound' 
                            ? 'bg-blue-500 text-white rounded-br-none' 
                            : 'bg-gray-100 text-gray-800 rounded-bl-none'
                        }`}
                      >
                        {/* Unread indicator */}
                        {message.direction === 'inbound' && !isRead && (
                          <div className="absolute -left-2 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-blue-500 rounded-full"></div>
                        )}
                        
                        {/* Email subject */}
                        {message.message_type === 'email' && message.subject && (
                          <div className={`text-sm font-medium mb-1 ${
                            message.direction === 'outbound' ? 'text-blue-100' : 'text-gray-600'
                          }`}>
                            {message.subject}
                          </div>
                        )}
                        
                        {/* Message content */}
                        <div className="text-sm break-words">
                          {message.message_type === 'email' 
                            ? getEmailPreview(message.body, 200)
                            : message.body
                          }
                        </div>
                        
                        {/* Timestamp */}
                        <div className={`text-xs mt-1 flex items-center ${
                          message.direction === 'outbound' ? 'text-blue-100' : 'text-gray-500'
                        }`}>
                          <Clock className="h-3 w-3 mr-1" />
                          {format(new Date(message.created_at), 'MMM d, h:mm a')}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Message Input */}
          <div className="bg-white p-3 sm:p-4 border-t border-gray-200">
            {/* Message Type Selector */}
            {availableReplyMethods.length > 1 && (
              <div className="flex justify-center mb-2 sm:mb-3">
                <div className="inline-flex p-1 bg-gray-200 rounded-full">
                  {availableReplyMethods.map((method) => (
                    <button
                      key={method.type}
                      className={`px-3 sm:px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
                        messageType === method.type
                          ? 'bg-white shadow-sm text-blue-600'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      onClick={() => setMessageType(method.type)}
                    >
                      <div className="flex items-center space-x-1">
                        {method.type === 'sms' ? (
                          <Phone className="h-3.5 w-3.5" />
                        ) : (
                          <Mail className="h-3.5 w-3.5" />
                        )}
                        <span>{method.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Input Area */}
            <div className="flex items-end space-x-2">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`Type a ${messageType === 'sms' ? 'message' : 'email'} to ${customerName}...`}
                className="flex-1 min-h-[60px] sm:min-h-[80px] resize-none bg-gray-50 rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={sendingMessage}
              />
              <button
                onClick={() => {
                  if (messageType === 'sms') {
                    handleSendSMS();
                  } else {
                    handleSendEmail();
                  }
                }}
                disabled={!replyText.trim() || sendingMessage}
                className="p-2 sm:p-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
} 